/**
 * Everything the player can do. Each action validates and either applies fully
 * and returns true, or changes nothing and returns false.
 */

import type { PlanetStat } from '../content/planets';
import { FIT_THRESHOLDS, FIT_MAX_LEVEL, fitLevel, resource, socketCost } from '../content/resources';
import { SLOT_COSTS, projectDef } from '../content/projects';
import { ADDITIONS } from '../content/flags';
import { MATRIX_BY_ID, ORIGIN, levelCost, matrixNode } from '../content/matrix';
import { hasFeature, visiblePlanets } from './projects';
import { recipeInputs } from './rooms';
import { affordableLevels, bulkUpgradeCost, sellPrice, unlockCost } from './economy';
import { fleetStats } from './stats';
import { earnGc } from './tick';
import { newSlot, type GameState } from './state';

/** The cheapest planet not yet owned. */
export function nextPlanetIndex(state: GameState): number {
  return state.run.planets.findIndex((p) => !p.owned);
}

/** Visible to Telescopes, so purchasable. As in IPM, visible planets can be bought in any order. */
export function isVisible(state: GameState, index: number): boolean {
  return index < visiblePlanets(state);
}

/** GC spent on planets and their upgrades counts toward Galaxy Value (IPM's prestige measure). */
function spend(state: GameState, gc: number): void {
  state.run.gc -= gc;
  state.run.galaxyValue += gc;
}

export function buyPlanet(state: GameState, index: number): boolean {
  const p = state.run.planets[index];
  if (!p || p.owned || !isVisible(state, index)) return false;
  const cost = unlockCost(state, fleetStats(state), index);
  if (state.run.gc < cost) return false;
  spend(state, cost);
  p.owned = true;
  return true;
}

/** Buys `count` levels, or as many as affordable when count is 'max'. */
export function upgradePlanet(state: GameState, index: number, stat: PlanetStat, count: number | 'max'): boolean {
  const p = state.run.planets[index];
  if (!p?.owned) return false;
  const stats = fleetStats(state);
  const level = p.levels[stat];
  const n = count === 'max' ? affordableLevels(state, stats, index, stat, level, state.run.gc) : count;
  if (n <= 0) return false;
  const cost = bulkUpgradeCost(state, stats, index, stat, level, n);
  if (state.run.gc < cost) return false;
  spend(state, cost);
  p.levels[stat] += n;
  return true;
}

export function sell(state: GameState, id: string, qty: number): boolean {
  const have = state.run.hold[id] ?? 0;
  const n = Math.min(have, qty);
  if (n <= 0) return false;
  state.run.hold[id] = have - n;
  earnGc(state, n * sellPrice(state, fleetStats(state), id));
  return true;
}

/** Every ore, or every alloy, in the hold. */
export function sellKind(state: GameState, kind: 'ore' | 'alloy'): boolean {
  let sold = false;
  for (const [id, qty] of Object.entries(state.run.hold)) {
    if (resource(id).kind === kind && qty > 0) sold = sell(state, id, qty) || sold;
  }
  return sold;
}

export type Workshop = 'smelter' | 'crafter';

/** IPM sells each recipe for GC, per galaxy; the first bar and first item are free. */
export function hasRecipe(state: GameState, id: string): boolean {
  return resource(id).recipe!.unlock === 0 || state.run.recipes.includes(id);
}

export function buyRecipe(state: GameState, id: string): boolean {
  const def = resource(id);
  if (!def.recipe || hasRecipe(state, id)) return false;
  if (!hasFeature(state, def.kind === 'alloy' ? 'smelter' : 'crafter')) return false;
  if (state.run.gc < def.recipe.unlock) return false;
  state.run.gc -= def.recipe.unlock;
  state.run.recipes.push(id);
  return true;
}

/** GC for the next smelter or crafter slot; null at the cap or before the workshop exists. */
export function slotCost(state: GameState, workshop: Workshop): number | null {
  const slots = workshop === 'smelter' ? state.run.smelters : state.run.crafters;
  if (slots.length === 0) return null;
  const base = SLOT_COSTS[slots.length];
  if (base === undefined) return null;
  const vendor = state.run.projects.some((id) => projectDef(id).effects.some((e) => e.kind === 'slotDiscount'));
  return vendor ? base * 0.5 : base;
}

export function buySlot(state: GameState, workshop: Workshop): boolean {
  const cost = slotCost(state, workshop);
  if (cost === null || state.run.gc < cost) return false;
  state.run.gc -= cost;
  (workshop === 'smelter' ? state.run.smelters : state.run.crafters).push(newSlot());
  return true;
}

/** Changing a running slot's recipe refunds what it consumed. */
export function setRecipe(state: GameState, workshop: Workshop, slotIndex: number, recipeId: string | null): boolean {
  const slots = workshop === 'smelter' ? state.run.smelters : state.run.crafters;
  const slot = slots[slotIndex];
  if (!slot) return false;
  if (recipeId) {
    const def = resource(recipeId);
    if (!def.recipe || (workshop === 'smelter') !== (def.kind === 'alloy')) return false;
    if (!hasRecipe(state, recipeId)) return false;
  }
  if (slot.running && slot.recipe) {
    for (const i of recipeInputs(state, slot.recipe)) {
      state.run.hold[i.id] = (state.run.hold[i.id] ?? 0) + i.qty;
    }
  }
  slot.recipe = recipeId;
  slot.running = false;
  slot.progress = 0;
  return true;
}

// ── Fittings ───────────────────────────────────────────────────────────────

/** Copies that would still raise this fitting; beyond the top threshold buys nothing. */
export function fitRoom(state: GameState, id: string): number {
  return Math.max(0, FIT_THRESHOLDS[FIT_MAX_LEVEL]! - (state.fittings[id] ?? 0));
}

/** Copies needed to reach the next fitting level, or 0 at max. */
export function copiesToNextLevel(state: GameState, id: string): number {
  const copies = state.fittings[id] ?? 0;
  const level = fitLevel(copies);
  if (level >= FIT_MAX_LEVEL) return 0;
  return FIT_THRESHOLDS[level + 1]! - copies;
}

export function feedFitting(state: GameState, id: string, qty: number): boolean {
  if (!ADDITIONS.fittings) return false;
  if (resource(id).kind !== 'item') return false;
  const n = Math.min(Math.floor(state.run.hold[id] ?? 0), qty, fitRoom(state, id));
  if (n <= 0) return false;
  const before = fitLevel(state.fittings[id] ?? 0);
  state.run.hold[id]! -= n;
  state.fittings[id] = (state.fittings[id] ?? 0) + n;
  // A first level drops straight into a free socket.
  if (before === 0 && !state.socketed.includes(id) && state.socketed.length < state.sockets) state.socketed.push(id);
  return true;
}

export function socketFitting(state: GameState, id: string): boolean {
  if (state.socketed.includes(id) || state.socketed.length >= state.sockets) return false;
  if (fitLevel(state.fittings[id] ?? 0) < 1) return false;
  state.socketed.push(id);
  return true;
}

export function unsocketFitting(state: GameState, id: string): boolean {
  const at = state.socketed.indexOf(id);
  if (at < 0) return false;
  state.socketed.splice(at, 1);
  return true;
}

export function buySocket(state: GameState): boolean {
  const cost = socketCost(state.sockets);
  if (cost === null || state.sc < cost) return false;
  state.sc -= cost;
  state.sockets += 1;
  return true;
}

// ── Matrix ─────────────────────────────────────────────────────────────────

export function matrixLevel(state: GameState, id: string): number {
  return state.matrix[id] ?? 0;
}

export function isAllocated(state: GameState, id: string): boolean {
  return id === ORIGIN || matrixLevel(state, id) > 0;
}

/** Can take another level: below its max, and (for a first level) touching an allocated node. */
export function canReach(state: GameState, id: string): boolean {
  const node = MATRIX_BY_ID.get(id);
  if (!node || id === ORIGIN) return false;
  const level = matrixLevel(state, id);
  if (level >= node.maxLevel) return false;
  return level > 0 || node.neighbours.some((n) => isAllocated(state, n));
}

/** VS for the node's next level, or null if it can't take one. */
export function nextLevelCost(state: GameState, id: string): number | null {
  return levelCost(matrixNode(id), matrixLevel(state, id));
}

/** Raises a Matrix node one level (the first level allocates it). */
export function allocateNode(state: GameState, id: string): boolean {
  if (!canReach(state, id)) return false;
  const cost = nextLevelCost(state, id);
  if (cost === null || state.vs < cost) return false;
  state.vs -= cost;
  state.matrix[id] = matrixLevel(state, id) + 1;
  return true;
}

/**
 * Lowering a level is always allowed above level 1. Removing the last level
 * needs every other allocated node to still reach the origin without it.
 */
export function canRefund(state: GameState, id: string): boolean {
  const level = matrixLevel(state, id);
  if (level <= 0) return false;
  if (level > 1) return true;
  const kept = new Set(Object.keys(state.matrix).filter((n) => n !== id && matrixLevel(state, n) > 0));
  const seen = new Set<string>([ORIGIN]);
  const queue = [ORIGIN];
  while (queue.length) {
    for (const next of matrixNode(queue.pop()!).neighbours) {
      if (kept.has(next) && !seen.has(next)) { seen.add(next); queue.push(next); }
    }
  }
  return [...kept].every((n) => seen.has(n));
}

/** Refunds the node's top level, full VS back, so trying a build costs nothing. */
export function refundNode(state: GameState, id: string): boolean {
  if (!canRefund(state, id)) return false;
  const level = matrixLevel(state, id);
  state.vs += levelCost(matrixNode(id), level - 1)!;
  if (level === 1) delete state.matrix[id];
  else state.matrix[id] = level - 1;
  return true;
}

/** Nodes with at least one level. */
export function allocatedNodes(state: GameState): number {
  return Object.values(state.matrix).filter((l) => l > 0).length;
}

// Rooms live in ./rooms; re-exported for scripts that import them from here.
export { upgradeRoom, nextRoomCost as roomCost } from './rooms';
